/**
 * useAuth.ts  -  Authentication hooks.
 * Handles login, logout, and boot-time session restoration.
 */

import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuthStore, type AuthUser } from "@/store/authStore";

interface LoginCredentials {
  email: string;
  password: string;
}
interface LoginResponse {
  accessToken: string;
  expiresIn: number;
}
interface MfaChallengeResp {
  requiresMfa: true;
  mfaToken: string;
}
type LoginApiResponse = LoginResponse | MfaChallengeResp;
interface MeResponse {
  user: AuthUser;
}

// ─── Login ────────────────────────────────────────────────────────────────────

export function useLogin(onMfaRequired: (mfaToken: string) => void) {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (creds: LoginCredentials) =>
      api.post<LoginApiResponse>("/auth/login", creds),

    onSuccess: async (data) => {
      if ("requiresMfa" in data) {
        onMfaRequired(data.mfaToken);
        return;
      }
      const { user } = await api.get<MeResponse>("/auth/me", {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });
      setAuth(data.accessToken, user);
      qc.invalidateQueries({ queryKey: ["me"] });
      navigate(roleHomePath(user.role));
    },
  });
}

// ─── MFA verify ──────────────────────────────────────────────────────────────

export function useVerifyMfa() {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: { mfaToken: string; code: string }) =>
      api.post<LoginResponse>("/auth/verify-mfa", body),

    onSuccess: async (data) => {
      const { user } = await api.get<MeResponse>("/auth/me", {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });
      setAuth(data.accessToken, user);
      qc.invalidateQueries({ queryKey: ["me"] });
      navigate(roleHomePath(user.role));
    },
  });
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function useLogout() {
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      clearAuth();
      qc.clear();
      navigate("/login");
    },
  });
}

// ─── Role → home path ─────────────────────────────────────────────────────────

export function roleHomePath(role: string): string {
  if (role === "platform_admin") return "/admin";
  if (role === "parent") return "/parent";
  return "/dashboard";
}

// ─── Session restore on boot ──────────────────────────────────────────────────

/**
 * Called once in App.tsx on mount.
 *
 * Strategy:
 *   1. If sessionStorage already has a user (Zustand hydrated), mark load done
 *      immediately so the spinner is skipped on page refresh.
 *   2. Always attempt a silent /auth/refresh in the background to rotate
 *      the cookie-based refresh token and get a fresh access token.
 *   3. On failure, clear auth and let RequireAuth redirect to /login.
 */
export function useSessionRestore() {
  const { setAuth, clearAuth, setLoading, user, accessToken } = useAuthStore();

  useEffect(() => {
    // If sessionStorage had auth state, Zustand already hydrated it
    // by the time the first useEffect runs  -  skip the spinner.
    if (user && accessToken) {
      setLoading(false);
      // Background refresh to rotate the cookie token silently
      void api
        .post<LoginResponse>("/auth/refresh")
        .then(async (data) => {
          const { user: freshUser } = await api.get<MeResponse>("/auth/me");
          setAuth(data.accessToken, freshUser);
        })
        .catch(() => {
          // Background refresh failed  -  keep existing sessionStorage state
          // for now; 401s will trigger the interceptor refresh later
        });
      return;
    }

    // No cached state  -  try cookie-based refresh (first visit after tab open)
    api
      .post<LoginResponse>("/auth/refresh")
      .then(async (data) => {
        const { user: freshUser } = await api.get<MeResponse>("/auth/me");
        setAuth(data.accessToken, freshUser);
      })
      .catch(() => {
        clearAuth();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
